import { CONNECTORS_UI_ENABLED } from "../../../../lib/featureFlags";
import { formatYmdLong } from "../../../../lib/dates";
import type { WizardRace } from "../../../../lib/raceSources";
import { useRovingRadio } from "../../../../hooks/useRovingRadio";
import type { DashboardProviderConnection } from "../../../../lib/dashboardApi";
import {
  connectorWizardDescAfterSetup,
  connectorWizardDescConnected
} from "../../../Connections/connectorOfferCopy";
import { LucideIcon, type LucideIconName } from "../../../FG/LucideIcon";
import { MethodChoiceCard } from "../../MethodChoiceCard";

/**
 * What the athlete answered on "Do you have a training plan?" (D17) — rides the
 * apply body as `trainingChoice`. Everything but "plan" is a plan-less journey.
 */
export type WizardTrainingChoice = "plan" | "connector" | "import" | "generate" | "build";

/** The tapped card. "I'll enter it manually" and "I'll build my own" are both
 *  the build choice — kept apart so only the card actually tapped reads checked. */
export type WizardTrainingCard = WizardTrainingChoice | "manual";

export function trainingChoiceOf(card: WizardTrainingCard): WizardTrainingChoice {
  return card === "manual" ? "build" : card;
}

/**
 * The wizard's Q2 "Do you have a training plan?" (D17): one screen, two
 * labelled groups — "Yes, I have one" (connect / import / enter manually) and
 * "No, I need one" (Fuel Goods plan on the race path / balanced week with no
 * race / build my own). One radiogroup across both groups: the headings are
 * plain text the arrow keys skip; tapping a card selects AND advances.
 */
export function MethodStep({
  noRace,
  selectedRace,
  weeksOut,
  raceDate,
  noPlansForDistance,
  card,
  setCard,
  onPick,
  jumpToRace,
  connectorOffered,
  connectorConnected,
  importOffered,
  connectorRequiresLogin = false
}: {
  /** No-race path (Q1 "Not right now"): no Fuel Goods plan, no race card; the
   *  balanced-week card takes the plan's place. */
  noRace: boolean;
  selectedRace: WizardRace | null;
  weeksOut: number;
  raceDate: string | null;
  noPlansForDistance: boolean;
  /** Selected card, or null when nothing is selected yet (guests). */
  card: WizardTrainingCard | null;
  /** Arrow-key selection (W-16) — selects WITHOUT advancing. */
  setCard: (card: WizardTrainingCard) => void;
  /** D-16 auto-advance: a tap selects the card and moves on. */
  onPick: (card: WizardTrainingCard) => void;
  /** D-19 — the race card's quiet "Change" link jumps back to the Race step. */
  jumpToRace: () => void;
  /** Registered providers on offer. Empty = nothing to show. */
  connectorOffered: DashboardProviderConnection[];
  /** The linked provider (server allows one). */
  connectorConnected: DashboardProviderConnection | null;
  /** The import flag is on — shows "Import my coach's plan". */
  importOffered: boolean;
  /** Guest: Connect card stays visible but disabled + login copy. */
  connectorRequiresLogin?: boolean;
}) {
  const connectorLabels = connectorOffered.map((c) => c.label);
  const showConnector =
    CONNECTORS_UI_ENABLED && (connectorOffered.length > 0 || connectorConnected != null);
  const showPlan = !noRace && !noPlansForDistance;
  // Guests can't start OAuth — keep the card as a teaser unless already linked.
  const connectorDisabled = connectorRequiresLogin && connectorConnected == null;
  type Card = {
    key: WizardTrainingCard;
    icon: LucideIconName;
    tone: "purple" | "amber" | "lime";
    title: string;
    pill?: string;
    desc: string;
    disabled?: boolean;
  };
  type Item = Card | { heading: string };
  const connectorDesc = connectorConnected
    ? connectorWizardDescConnected(connectorConnected.label)
    : connectorWizardDescAfterSetup(connectorLabels);
  const yes: Card[] = [
    ...(showConnector
      ? [
          {
            key: "connector",
            icon: "refresh-cw",
            tone: "amber",
            title: "Connect my training app",
            desc: connectorDisabled
              ? `${connectorDesc} Available when logged in.`
              : connectorDesc,
            disabled: connectorDisabled
          } satisfies Card
        ]
      : []),
    ...(importOffered
      ? [
          {
            key: "import",
            icon: "upload",
            tone: "amber",
            title: "Import my coach's plan",
            desc: "From a coach or another app — paste the text or upload a CSV (more formats soon). AI parses, you approve."
          } satisfies Card
        ]
      : []),
    {
      key: "manual",
      icon: "pencil",
      tone: "purple",
      title: "I'll enter it manually",
      desc: "Add your sessions to the calendar yourself — we fuel each one."
    }
  ];
  const no: Card[] = [
    ...(showPlan
      ? [
          {
            key: "plan",
            icon: "star",
            tone: "lime",
            title: "Use a Fuel Goods training plan",
            pill: "Recommended",
            desc: "A 12–16 week periodized plan built to your goal time — base, build, peak, taper."
          } satisfies Card
        ]
      : []),
    ...(noRace
      ? [
          {
            key: "generate",
            icon: "sparkles",
            tone: "lime",
            title: "Build a balanced week for me",
            desc: "A balanced week of sessions from your settings — tweak anything after."
          } satisfies Card
        ]
      : []),
    {
      key: "build",
      icon: "calendar",
      tone: "purple",
      title: "I'll build my own",
      desc: "Start with an open calendar — add sessions or presets as you go."
    }
  ];
  // ONE flat list (headings included) so the roving radio's index matches the
  // radiogroup's direct children; headings are never selectable.
  const items: Item[] = [{ heading: "Yes, I have one" }, ...yes, { heading: "No, I need one" }, ...no];
  const isEnabledCard = (it: Item): it is Card => "key" in it && !it.disabled;
  // The default plan pick on a no-plan distance reads as build-my-own; with no
  // race / guest nothing starts selected (the plan card isn't offered / no pick yet).
  const selectedKey: WizardTrainingCard | null =
    card === "plan" && noPlansForDistance ? "build" : card;
  const selectedIndex = items.findIndex((it) => "key" in it && it.key === selectedKey);
  const firstEnabledIndex = items.findIndex(isEnabledCard);
  const rove = useRovingRadio({
    length: items.length,
    // The tab stop: the selected card, else the first enabled CARD.
    selectedIndex: selectedIndex >= 0 ? selectedIndex : firstEnabledIndex,
    isSelectable: (n) => {
      const it = items[n];
      return it != null && isEnabledCard(it);
    },
    onArrowSelect: (n) => {
      const it = items[n];
      if (it && isEnabledCard(it)) setCard(it.key);
    }
  });

  return (
    <div className="fgp-wizard-panel">
      {/* D-19 — the standard race summary card (same pattern as the Race step)
          with a quiet Change link back to the Race step. */}
      {!noRace && selectedRace && (
        <div className="fgp-wizard-race-card" style={{ marginTop: 0 }}>
          <span className="fgo-event-opt__ico" aria-hidden>
            <LucideIcon name="flag" size={18} color="#B45309" strokeWidth={2} />
          </span>
          <span className="fgo-event-opt__body">
            <span className="fgo-event-opt__title">{selectedRace.name}</span>
            <span className="fgo-event-opt__meta">
              {[
                formatYmdLong(selectedRace.dateYmd),
                selectedRace.distanceLabel,
                weeksOut > 0
                  ? `${weeksOut} ${weeksOut === 1 ? "week" : "weeks"} out`
                  : raceDate
                    ? "less than a week away"
                    : null
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
          <button
            type="button"
            className="fgp-wz-ramplink"
            style={{ flexShrink: 0, alignSelf: "center" }}
            onClick={jumpToRace}>
            Change
          </button>
        </div>
      )}
      <div className="fgp-wz-methodlist" role="radiogroup" aria-label="Do you have a training plan?">
        {items.map((it, i) =>
          "key" in it ? (
            <MethodChoiceCard
              key={it.key}
              on={it.key === selectedKey}
              disabled={it.disabled}
              icon={it.icon}
              tone={it.tone}
              title={it.title}
              pill={it.pill}
              desc={it.desc}
              tabIndex={rove.tabIndexFor(i)}
              onKeyDown={(e) => rove.onKeyDown(e, i)}
              onSelect={() => {
                if (it.disabled) return;
                onPick(it.key);
              }}
            />
          ) : (
            <div key={it.heading} className="fgp-wz-fuel__label">
              {it.heading}
            </div>
          )
        )}
      </div>
      {noPlansForDistance && (
        <p className="fgp-wizard-race-hint" style={{ marginTop: 14 }}>
          We don&apos;t have a Fuel Goods plan for this event type yet — pick any other option
          and we&apos;ll fuel it.
        </p>
      )}
    </div>
  );
}
